import { create } from 'zustand';
import type { ConfiguratorState, HistoryEntry, EffectType, LogoInstance, TextInstance } from '../types/index';
import { getDefaultModels } from '../utils/modelDiscovery';

const MAX_HISTORY = 50; // Limit history to prevent memory issues

// Select a random model from the discovery list
const getRandomModel = (): { id: string; glbPath: string } => {
    const models = getDefaultModels();
    if (models.length === 0) return { id: 'pen', glbPath: '/models/Pen.glb' }; // Fallback
    const randomIndex = Math.floor(Math.random() * models.length);
    return { id: models[randomIndex].id, glbPath: models[randomIndex].glbPath };
};

// Generate once at module load
const initialRandomModel = getRandomModel();

// Helper function to create a history entry from current state
const createHistoryEntry = (state: ConfiguratorState): HistoryEntry => ({
    colorCustomizations: [...state.colorCustomizations],
    logoInstances: state.logoInstances.map(li => ({ ...li })),
    textInstances: state.textInstances.map(ti => ({ ...ti })),
    timestamp: Date.now(),
});

// Helper function to push to history
const pushHistory = (state: ConfiguratorState, updates: Partial<ConfiguratorState> = {}) => {
    // Merge updates into state for history entry creation
    // We cast to ConfiguratorState because we know 'state' has all required fields
    // and 'updates' just overrides some of them.
    const effectiveState = { ...state, ...updates } as ConfiguratorState;

    const newEntry = createHistoryEntry(effectiveState);
    const newHistory = state.history.slice(0, state.historyIndex + 1);
    newHistory.push(newEntry);

    // Limit history size
    if (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
    }

    return {
        history: newHistory,
        historyIndex: newHistory.length - 1,
    };
};

export const useConfiguratorStore = create<ConfiguratorState>((set, get) => {
    // Initialize with empty state as first history entry
    const initialState: HistoryEntry = {
        colorCustomizations: [],
        logoInstances: [],
        textInstances: [],
        timestamp: Date.now(),
    };

    return {
        // Model state
        modelLoaded: false,
        modelParts: [],
        glbUrl: initialRandomModel.glbPath, // Random model on each load

        // Print areas
        printAreas: [],
        activePrintAreaId: null,

        // Logos
        logos: [],
        logoInstances: [],
        activeLogoInstanceId: null,

        // Text instances
        textInstances: [],
        activeTextInstanceId: null,

        // Colors
        colorCustomizations: [],

        // Current model - synced with random glbUrl
        currentModel: initialRandomModel.id,

        // UI state
        toolMode: null,
        selectedPart: null,
        currentView: 'front',
        isLoading: true, // Start loading by default directly for smoother first load
        error: null,
        showPrintAreaBorders: true,
        isInteracting: false,
        lightingPreset: 'warehouse', // Default starting preset
        sceneBackground: '#E5E7EB', // Default little grey
        canZoomIn: true,
        canZoomOut: true,

        // Video Recording
        isRecording: false,

        // Wireframe Mode (for PDF Proof)
        isWireframeMode: false,

        // History state - Initialize with initial state
        history: [initialState],
        historyIndex: 0,

        // Actions
        setModelLoaded: (loaded) => set({ modelLoaded: loaded }),
        setModelParts: (parts) => set({ modelParts: parts }),
        setGlbUrl: (url) => set({ glbUrl: url }),
        setPrintAreas: (areas) => set({ printAreas: areas }),
        
        setWireframeMode: (isWireframeMode: boolean) => set({ isWireframeMode }),

        setActivePrintArea: (id) => set({ activePrintAreaId: id }),

        addLogo: (logo) => set((state) => ({
            logos: [...state.logos, logo]
        })),

        removeLogo: (id) => set((state) => ({
            logos: state.logos.filter(l => l.id !== id),
            logoInstances: state.logoInstances.filter(li => li.logoId !== id)
        })),

        addLogoInstance: (instanceInput) => set((state) => {
            const maxLayerOrder = Math.max(
                0,
                ...state.logoInstances.map(l => l.layerOrder),
                ...state.textInstances.map(t => t.layerOrder)
            );
            const logoObj = state.logos.find(l => l.id === instanceInput.logoId);
            const name = logoObj?.name || `Logo ${state.logoInstances.length + 1}`;
            
            const instance: LogoInstance = {
                ...instanceInput,
                layerOrder: maxLayerOrder + 1,
                name
            };

            const newInstances = [...state.logoInstances, instance];
            return {
                logoInstances: newInstances,
                activeLogoInstanceId: instance.id,
                ...pushHistory(state, { logoInstances: newInstances }),
            };
        }),

        updateLogoInstance: (id, updates) => set((state) => {
            const newInstances = state.logoInstances.map(li =>
                li.id === id ? { ...li, ...updates } : li
            );
            return {
                logoInstances: newInstances,
                // Skip history during drag interaction to prevent flicker
                ...(state.isInteracting ? {} : pushHistory(state, { logoInstances: newInstances })),
            };
        }),

        removeLogoInstance: (id) => set((state) => {
            const newInstances = state.logoInstances.filter(li => li.id !== id);
            return {
                logoInstances: newInstances,
                activeLogoInstanceId: state.activeLogoInstanceId === id ? null : state.activeLogoInstanceId,
                ...pushHistory(state, { logoInstances: newInstances }),
            };
        }),

        clearLogoInstances: () => set((state) => {
            const newInstances: LogoInstance[] = [];
            return {
                logoInstances: newInstances,
                activeLogoInstanceId: null,
                ...pushHistory(state, { logoInstances: newInstances }),
            };
        }),

        setLogoInstances: (instances) => set({ logoInstances: instances }),

        setActiveLogoInstance: (id) => set({ activeLogoInstanceId: id }),

        // Text instance actions
        addTextInstance: (printAreaId: string, text: string, initialPosition?: { x: number; y: number }, options?: { rotation?: number }) => set((state) => {
            const maxLayerOrder = Math.max(
                0,
                ...state.logoInstances.map(l => l.layerOrder),
                ...state.textInstances.map(t => t.layerOrder)
            );
            const name = text.length > 15 
                ? `${text.substring(0, 15)}...` 
                : text || `Text ${state.textInstances.length + 1}`;

            const newInstance: TextInstance = {
                id: `text-${Date.now()}`,
                printAreaId,
                text,
                fontFamily: 'Inter',
                fontSize: 24,
                fontStyle: [],
                textAlign: 'center',
                color: '#000000',
                position: initialPosition ?? { x: 0.5, y: 0.5 },
                scale: 1,
                rotation: options?.rotation ?? 0,
                effect: 'none' as EffectType,
                timestamp: Date.now(),
                layerOrder: maxLayerOrder + 1,
                name
            };

            const newInstances = [...state.textInstances, newInstance];
            return {
                textInstances: newInstances,
                activeTextInstanceId: newInstance.id,
                ...pushHistory(state, { textInstances: newInstances }),
            };
        }),

        updateTextInstance: (id, updates) => set((state) => {
            const newInstances = state.textInstances.map(ti =>
                ti.id === id ? { ...ti, ...updates } : ti
            );
            return {
                textInstances: newInstances,
                // Skip history during drag interaction to prevent flicker
                ...(state.isInteracting ? {} : pushHistory(state, { textInstances: newInstances })),
            };
        }),

        removeTextInstance: (id) => set((state) => {
            const newInstances = state.textInstances.filter(ti => ti.id !== id);
            return {
                textInstances: newInstances,
                activeTextInstanceId: state.activeTextInstanceId === id ? null : state.activeTextInstanceId,
                ...pushHistory(state, { textInstances: newInstances }),
            };
        }),

        setTextInstances: (instances) => set({ textInstances: instances }),

        setActiveTextInstance: (id) => set({ activeTextInstanceId: id }),

        updatePartColor: (partName, color) => set((state) => {
            let newColors;
            const existing = state.colorCustomizations.find(c => c.partName === partName);
            if (existing) {
                newColors = state.colorCustomizations.map(c =>
                    c.partName === partName ? { ...c, color } : c
                );
            } else {
                newColors = [...state.colorCustomizations, { partName, color }];
            }

            return {
                colorCustomizations: newColors,
                ...pushHistory(state, { colorCustomizations: newColors }),
            };
        }),

        updatePrintAreaColor: (printAreaId, color) => set((state) => {
            const newPrintAreas = state.printAreas.map(pa => 
                pa.id === printAreaId ? { ...pa, backgroundColor: color || undefined } : pa
            );
            return {
                printAreas: newPrintAreas
            };
            // Note: We are strictly NOT pushing history for print area background color yet as it's not deeply integrated into history types
            // If we want it in history, we'd need to add 'printAreas' explicitly to history state, which is a larger refactor.
            // For now, it stays as current session state.
        }),

        removeColorCustomization: (partName) => set((state) => {
            const newColors = state.colorCustomizations.filter(c => c.partName !== partName);
            return {
                colorCustomizations: newColors,
                ...pushHistory(state, { colorCustomizations: newColors }),
            };
        }),

        updateAllColors: (color) => set((state) => {
            // Use a special marker to indicate "all parts"
            // This will be handled specially in ModelLoader
            const newColors = [{ partName: '__ALL__', color }];
            return {
                colorCustomizations: newColors,
                ...pushHistory(state, { colorCustomizations: newColors }),
            };
        }),

        clearColorCustomizations: () => set((state) => {
            const newColors: any[] = [];
            return {
              colorCustomizations: newColors,
              ...pushHistory(state, { colorCustomizations: newColors }),
            };
        }),

        setToolMode: (mode) => set({ toolMode: mode }),

        setSelectedPart: (partName) => set({ selectedPart: partName }),

        setCurrentView: (view) => set({ currentView: view }),

        setCurrentModel: (model) => set({ currentModel: model }),

        setLoading: (loading) => set({ isLoading: loading }),

        setError: (error) => set({ error }),

        togglePrintAreaBorders: () => set((state) => ({ showPrintAreaBorders: !state.showPrintAreaBorders })),

        setIsInteracting: (isInteracting) => set({ isInteracting }),

        setLightingPreset: (preset) => set({ lightingPreset: preset }),
        setSceneBackground: (color) => set({ sceneBackground: color }),
        setZoomCapabilities: (canZoomIn, canZoomOut) => set({ canZoomIn, canZoomOut }),
        setIsRecording: (isRecording) => set({ isRecording }),

        // History actions
        undo: () => set((state) => {
            if (state.historyIndex > 0) {
                const prevEntry = state.history[state.historyIndex - 1];
                return {
                    colorCustomizations: [...prevEntry.colorCustomizations],
                    logoInstances: prevEntry.logoInstances.map(li => ({ ...li })),
                    textInstances: prevEntry.textInstances.map(ti => ({ ...ti })),
                    historyIndex: state.historyIndex - 1,
                };
            }
            return state;
        }),

        redo: () => set((state) => {
            if (state.historyIndex < state.history.length - 1) {
                const nextEntry = state.history[state.historyIndex + 1];
                return {
                    colorCustomizations: [...nextEntry.colorCustomizations],
                    logoInstances: nextEntry.logoInstances.map(li => ({ ...li })),
                    textInstances: nextEntry.textInstances.map(ti => ({ ...ti })),
                    historyIndex: state.historyIndex + 1,
                };
            }
            return state;
        }),

        reorderLayer: (type: 'logo' | 'text', id: string, newLayerOrder: number) => set((state) => {
            // Flatten all instances
            const allItems = [
                ...state.logoInstances.map(l => ({ ...l, type: 'logo' as const })),
                ...state.textInstances.map(t => ({ ...t, type: 'text' as const }))
            ].sort((a, b) => a.layerOrder - b.layerOrder);

            const oldIndex = allItems.findIndex(item => item.id === id && item.type === type);
            if (oldIndex === -1) return state; // Not found

            const itemToMove = allItems.splice(oldIndex, 1)[0];
            
            // newLayerOrder acts as the new index within the sorted list, so we insert it at newLayerOrder
            // ensure it's clamped
            const clampedIndex = Math.max(0, Math.min(newLayerOrder, allItems.length));
            allItems.splice(clampedIndex, 0, itemToMove);

            // Reassign layer orders consecutively from 1 to N
            const newLogos: LogoInstance[] = [];
            const newTexts: TextInstance[] = [];

            allItems.forEach((item, idx) => {
                const updatedItem = { ...item, layerOrder: idx + 1 };
                if (updatedItem.type === 'logo') {
                    const { type, ...rest } = updatedItem;
                    newLogos.push(rest as LogoInstance);
                } else {
                    const { type, ...rest } = updatedItem;
                    newTexts.push(rest as TextInstance);
                }
            });

            return {
                logoInstances: newLogos,
                textInstances: newTexts,
                ...pushHistory(state, { logoInstances: newLogos, textInstances: newTexts })
            };
        }),

        renameLayer: (type: 'logo' | 'text', id: string, newName: string) => set((state) => {
            if (type === 'logo') {
                const newInstances = state.logoInstances.map(li =>
                    li.id === id ? { ...li, name: newName } : li
                );
                return { logoInstances: newInstances, ...pushHistory(state, { logoInstances: newInstances }) };
            } else {
                const newInstances = state.textInstances.map(ti =>
                    ti.id === id ? { ...ti, name: newName } : ti
                );
                return { textInstances: newInstances, ...pushHistory(state, { textInstances: newInstances }) };
            }
        }),

        selectLayer: (type: 'logo' | 'text' | null, id: string | null) => set((state) => {
            if (type === 'logo') {
                return {
                    activeLogoInstanceId: id,
                    activeTextInstanceId: null, // clear other
                    toolMode: id ? 'select' : state.toolMode
                };
            } else if (type === 'text') {
                return {
                    activeTextInstanceId: id,
                    activeLogoInstanceId: null, // clear other
                    toolMode: id ? 'select' : state.toolMode
                };
            } else {
                // Clear both
                return {
                    activeLogoInstanceId: null,
                    activeTextInstanceId: null
                };
            }
        }),

        canUndo: () => {
            const state = get();
            return state.historyIndex > 0;
        },

        canRedo: () => {
            const state = get();
            return state.historyIndex < state.history.length - 1;
        },
    };
});
